<template>
  <div class="p-6 w-full">
    <div v-if="loading" class="flex justify-center items-center" style="min-height: 400px">
      <i class="pi pi-spin pi-spinner text-4xl text-gray-500"></i>
    </div>

    <!-- Event not found or not live -->
    <div
      v-else-if="eventLoadError"
      class="flex flex-col justify-center items-center"
      style="height: calc(100vh - 250px)"
    >
      <i class="pi pi-exclamation-circle text-4xl text-gray-400 mb-4"></i>
      <p class="text-gray-600 dark:text-gray-300 text-lg">{{ eventLoadError }}</p>
    </div>

    <!-- Multiple live events — picker -->
    <div
      v-else-if="liveEventsPicker.length"
      class="flex flex-col justify-center items-center max-w-lg mx-auto"
      style="min-height: calc(100vh - 250px)"
    >
      <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Select a live event</h2>
      <p class="text-gray-500 dark:text-gray-400 text-sm mb-6">
        Multiple events are live right now. Choose one to open its control panel.
      </p>
      <ul class="w-full space-y-2">
        <li v-for="event in liveEventsPicker" :key="event.id">
          <router-link
            :to="`/events/live/${event.id}`"
            class="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 hover:border-teal-500 dark:hover:border-teal-400 transition-colors"
          >
            <i class="pi pi-circle-fill text-green-500 text-xs"></i>
            <span class="font-medium text-gray-900 dark:text-white">{{ event.name }}</span>
          </router-link>
        </li>
      </ul>
    </div>

    <!-- No Active Event - Show Sleeping Cat -->
    <div
      v-else-if="!activeEvent"
      class="flex flex-col justify-center items-center"
      style="height: calc(100vh - 250px)"
    >
      <SleepingCat />
      <DancingCat />
      <div v-if="nextEvent" class="mt-1 text-center">
        <p class="text-gray-600 dark:text-gray-300 text-sm">
          Next event: <span class="font-semibold">{{ nextEvent.name }}</span>
        </p>
        <p class="text-gray-500 dark:text-gray-400 text-sm">
          {{ formatEventStartTime(nextEvent) }}
        </p>
      </div>
    </div>

    <!-- Active Event Information -->
    <div v-else class="space-y-6">
      <!-- Event Header -->
      <div
        class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
      >
        <div class="mb-4">
          <div
            class="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-medium mb-4"
          >
            <i class="pi pi-circle-fill text-xs"></i>
            <span>Live Now</span>
          </div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {{ activeEvent.name }}
          </h1>
        </div>

        <!-- Voting Status -->
        <div
          class="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="font-semibold text-blue-900 dark:text-blue-100">
                Voting is {{ votingStatusText }}
              </p>
              <p class="text-sm text-blue-700 dark:text-blue-200 mt-1">
                <span v-if="votingStartTime">Started: {{ formatDateTime(votingStartTime) }}</span>
                <span v-if="votingEndTime" class="ml-4"
                  >Ends: {{ formatDateTime(votingEndTime) }}</span
                >
              </p>
            </div>
            <i class="pi pi-clock text-2xl text-blue-600 dark:text-blue-300"></i>
          </div>
        </div>

        <!-- Setlist Set Timer -->
        <div
          v-if="isSetlistActive && currentSetDuration > 0"
          class="mt-4 p-4 rounded-lg border"
          :class="{
            'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800':
              setRemainingSeconds >
              ((setlistSettings?.defaultSongTime || 240) + (setlistSettings?.pauseTime || 30)) * 2,
            'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800':
              setRemainingSeconds > 0 &&
              setRemainingSeconds <=
                ((setlistSettings?.defaultSongTime || 240) + (setlistSettings?.pauseTime || 30)) *
                  2,
            'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800':
              setRemainingSeconds <= 0
          }"
        >
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                Set {{ (activeEvent.currentSetIndex || 0) + 1 }} of
                {{ activeEvent.setlistDurations?.length || 0 }}
                <span class="ml-2 text-xs text-gray-500">({{ currentSetDuration }} min)</span>
              </p>
              <p class="text-2xl font-bold mt-1" :class="setRemainingUrgency">
                <i class="pi pi-stopwatch mr-1"></i>
                {{ setRemainingSeconds > 0 ? setRemainingFormatted : "Set time over" }}
              </p>
            </div>
            <Button
              label="Prepare Next Set"
              :icon="isStartingExtraSet ? 'pi pi-plus-circle' : 'pi pi-forward'"
              severity="info"
              size="small"
              class="w-full sm:w-auto"
              :disabled="setRemainingSeconds > 0"
              @click="handleStartNextSet"
            />
          </div>
        </div>

        <!-- Prepared Set Banner (set prepared, waiting for first song) -->
        <div
          v-if="isSetPreparedAwaitingStart && activeEvent.setlistEnabled"
          class="mt-4 p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
        >
          <div class="flex items-start gap-3">
            <i class="pi pi-check-circle text-blue-600 dark:text-blue-400 text-xl mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                Set {{ preparedSetNumber }} prepared
                <span v-if="currentSetDuration > 0" class="ml-2 text-xs text-gray-500">
                  ({{ currentSetDuration }} min)
                </span>
              </p>
              <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {{
                  currentSetDuration > 0
                    ? `${currentSetDuration} min timer will start with first song`
                    : "No timer — ready to start"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Currently Playing Song Display -->
      <div v-if="currentlyPlayingSong && !isKaraokeLive" class="mb-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border-2 p-4 border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <!-- Song Info -->
            <div class="flex-1 min-w-0">
              <span
                class="text-xs uppercase tracking-widest font-semibold text-green-600 dark:text-green-400 mb-1 block animate-pulse"
              >
                Now Playing
              </span>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white leading-snug">
                {{ currentlyPlayingSong.title }}
              </h3>
              <p
                v-if="currentlyPlayingSong.author"
                class="text-gray-600 dark:text-gray-400 text-sm mt-0.5"
              >
                {{ currentlyPlayingSong.author }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                Performed at: {{ formatDateTime(currentlyPlayingSong.performedAt) }}
              </p>
            </div>
            <!-- Set End Button (Admin only) -->
            <div v-if="authStore.isAdmin" class="shrink-0">
              <button
                class="w-full sm:w-auto min-w-[10rem] px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                @click="handleSetEnd(currentlyPlayingSong.id)"
              >
                <i class="pi pi-stop-circle"></i>
                Set end
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Up Next Song Display -->
      <div v-if="upNextSong && !isKaraokeLive" class="mb-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border-4 border-double p-4 border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <!-- Song Info -->
            <div class="flex-1 min-w-0">
              <span
                class="text-xs uppercase tracking-widest font-semibold text-amber-600 dark:text-amber-400 mb-1 block"
              >
                Up Next
              </span>
              <h3 class="text-xl font-semibold text-gray-900 dark:text-white leading-snug">
                {{ upNextSong.title }}
              </h3>
              <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm mt-0.5">
                {{ upNextSong.author }}
              </p>
            </div>
            <!-- Unlock Button (Admin only) -->
            <div v-if="authStore.isAdmin" class="shrink-0">
              <button
                :disabled="clearingUpNext"
                class="w-full sm:w-auto min-w-[10rem] px-4 py-2 bg-gray-500 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                @click="handleClearUpNext"
              >
                <i :class="clearingUpNext ? 'pi pi-spin pi-spinner' : 'pi pi-unlock'"></i>
                Unlock
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Admin Controls (Admin Only) -->
      <div v-if="authStore.user?.role === 'admin'" class="mb-6 space-y-4">
        <template v-if="!isKaraokeLive">
        <div v-if="showStartSetButton">
          <Button
            :label="startSetButtonLabel"
            :icon="startSetButtonIcon"
            :severity="startSetButtonSeverity"
            size="large"
            class="w-full py-4 text-lg font-semibold"
            :loading="playingNextSong"
            @click="handlePlayNextSong"
          />
          <p
            v-if="startSetHelperText"
            class="mt-2 text-sm text-center"
            :class="
              isSetPreparedAwaitingStart
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-amber-600 dark:text-amber-400'
            "
          >
            {{ startSetHelperText }}
          </p>
        </div>
        <!-- Play without queueing (only shows when autoQueueNext is enabled and there's a current song) -->
        <Button
          v-if="activeEvent.autoQueueNext && hasCurrentSong"
          label="Play next song w/o queue"
          icon="pi pi-step-forward"
          severity="warn"
          size="large"
          class="w-full py-4 text-lg font-semibold !bg-amber-500 !text-white !border-amber-500 hover:!bg-amber-600 hover:!border-amber-600 dark:!bg-amber-600 dark:!border-amber-600 dark:hover:!bg-amber-700 dark:hover:!border-amber-700"
          :loading="playingNextSongNoQueue"
          @click="handlePlayNextSongNoQueue"
        />
        <Button
          :label="activeEvent.isVotingDisabled ? 'Resume Voting' : 'Stop All Voting'"
          :icon="activeEvent.isVotingDisabled ? 'pi pi-play' : 'pi pi-stop-circle'"
          :severity="activeEvent.isVotingDisabled ? 'success' : 'danger'"
          size="large"
          class="w-full py-4 text-lg font-semibold"
          :loading="togglingVoting"
          @click="toggleVoting"
        />
        </template>
        <Button
          label="End Event"
          icon="pi pi-times-circle"
          severity="danger"
          outlined
          size="large"
          class="w-full py-4 text-lg font-semibold"
          :loading="endingEvent"
          @click="confirmEndEvent"
        />
      </div>

      <!-- Tabs and Song Content -->
      <div v-if="songs.length > 0">
        <!-- View Toggle Tabs -->
        <div class="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700">
          <button
            :class="[
              'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
              activeView === 'vote'
                ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
            ]"
            @click="activeView = 'vote'"
          >
            <i class="pi pi-heart mr-2"></i>
            Vote
          </button>
          <button
            :class="[
              'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
              activeView === 'results'
                ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
            ]"
            @click="activeView = 'results'"
          >
            <i class="pi pi-chart-bar mr-2"></i>
            Results
          </button>
          <button
            :class="[
              'px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px',
              activeView === 'performed'
                ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
            ]"
            @click="activeView = 'performed'"
          >
            <i class="pi pi-check-circle mr-2"></i>
            Performed
          </button>
        </div>

        <!-- Voting View -->
        <div v-if="activeView === 'vote'">
          <!-- Filters Section -->
          <div
            v-if="songs.length > 0"
            class="mb-8 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Search/Title Filter -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Search</label
                >
                <IconField icon-position="left" class="w-full">
                  <InputIcon class="pi pi-search text-gray-400 dark:text-gray-500" />
                  <InputText
                    v-model="filters.title"
                    placeholder="Title or artist..."
                    class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </IconField>
              </div>

              <!-- Category Filter -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Category</label
                >
                <Select
                  v-model="filters.category"
                  :options="categoryOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="All categories"
                  class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                  :show-clear="true"
                />
              </div>

              <!-- Sort by Votes -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Sort</label
                >
                <Select
                  v-model="filters.sortBy"
                  :options="sortOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select..."
                  class="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm"
                />
              </div>
            </div>
          </div>

          <div v-if="filteredSongs.length === 0" class="text-center py-16">
            <div
              class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <i class="pi pi-filter text-3xl text-gray-400"></i>
            </div>
            <p class="text-gray-600 dark:text-gray-400 text-lg mb-4">No songs match your filters</p>
            <button
              class="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium transition-colors"
              @click="clearFilters"
            >
              Clear filters
            </button>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SongCard
              v-for="song in filteredSongs"
              :key="song.id"
              :song="song"
              :event-id="String(activeEvent.id)"
              :vote-count="getVoteCount(song.id)"
              :has-voted="isKaraokeLive ? false : hasUserVoted(song.id)"
              :is-performed="song.isPerformed"
              :voting-enabled="true"
              :show-mark-performed="false"
              :show-vote-count="!isKaraokeLive"
              :show-vote-button="true"
              :queue-action-label="isKaraokeLive ? 'Add to queue' : ''"
              @vote="handleVote"
            />
          </div>
        </div>

        <!-- Results View -->
        <div v-else-if="activeView === 'results'">
          <div v-if="isKaraokeLive" class="space-y-3">
            <div
              v-if="!karaokeNowPlaying && karaokeQueueEntries.length === 0"
              class="text-center py-16"
            >
              <p class="text-gray-600 dark:text-gray-400 text-lg">No songs in the karaoke queue yet</p>
            </div>
            <KaraokeQueueRow
              v-if="karaokeNowPlaying"
              :entry="karaokeNowPlaying"
              :is-now-playing="true"
              :show-admin-actions="isStaffUser"
              @mark-performed="handleKaraokeMarkPerformed"
            />
            <KaraokeQueueRow
              v-for="(entry, index) in karaokeQueueEntries"
              :key="entry.voteId"
              :entry="entry"
              :queue-number="index + 1"
              :show-admin-actions="isStaffUser"
              :disable-start="!!karaokeNowPlaying"
              @start-song="handleKaraokeStartSong"
            />
          </div>

          <template v-else>
          <div v-if="sortedResults.length === 0 && !upNextSong" class="text-center py-16">
            <div
              class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <i class="pi pi-chart-bar text-3xl text-gray-400"></i>
            </div>
            <p class="text-gray-600 dark:text-gray-400 text-lg">No votes have been cast yet</p>
          </div>

          <div v-else class="space-y-3">
            <!-- Up Next Song (shown as song 0 at the top) -->
            <div
              v-if="upNextSong"
              class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all border-amber-400 dark:border-amber-500 bg-amber-50/30 dark:bg-amber-900/20 ring-2 ring-amber-300 dark:ring-amber-500"
            >
              <div class="results-card-layout">
                <!-- Up Next Icon -->
                <div class="results-card-rank">
                  <div
                    class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-amber-500 dark:bg-amber-600"
                    title="Up Next"
                  >
                    <i class="pi pi-forward text-2xl"></i>
                  </div>
                </div>

                <!-- Song Info -->
                <div class="results-card-info min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                      {{ upNextSong.title }}
                    </h3>
                  </div>
                  <p v-if="upNextSong.author" class="text-gray-600 dark:text-gray-400 text-sm">
                    {{ upNextSong.author }}
                  </p>
                </div>

                <!-- Up Next Badge -->
                <div class="results-card-votes text-center">
                  <span
                    class="inline-block px-4 py-2 text-sm bg-amber-500 text-white rounded-full font-bold uppercase tracking-wide"
                  >
                    UP NEXT
                  </span>
                </div>

                <!-- Mark Performed Button (Admin only) -->
                <div class="results-card-button">
                  <button
                    v-if="authStore.isAdmin && !getIsPerformed(upNextSong.id)"
                    class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                    @click="handleMarkPerformed(upNextSong.id)"
                  >
                    <i class="pi pi-check-circle"></i>
                    Mark Performed
                  </button>
                </div>
              </div>
            </div>

            <template v-for="(song, index) in sortedResultsFiltered" :key="song.id">
              <SetEndDivider v-if="index === setEndLineIndex" label="Approximate set end" />
              <div
                class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all"
                :class="{
                  'border-teal-400 dark:border-teal-500 bg-teal-50/20 dark:bg-teal-900/10':
                    index === 0,
                  'border-teal-500 dark:border-teal-600 bg-teal-50/15 dark:bg-teal-900/8':
                    index === 1,
                  'border-teal-600 dark:border-teal-700 bg-teal-50/10 dark:bg-teal-900/6':
                    index === 2,
                  'border-teal-700 dark:border-teal-800 bg-teal-50/5 dark:bg-teal-900/4':
                    index === 3,
                  'border-teal-800 dark:border-teal-900': index >= 4
                }"
              >
                <div class="results-card-layout">
                  <!-- Rank Badge -->
                  <div class="results-card-rank">
                    <div
                      class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white"
                      :class="{
                        'bg-teal-400 dark:bg-teal-500': index === 0,
                        'bg-teal-500 dark:bg-teal-600': index === 1,
                        'bg-teal-600 dark:bg-teal-700': index === 2,
                        'bg-teal-700 dark:bg-teal-800': index === 3,
                        'bg-teal-800 dark:bg-teal-900': index >= 4
                      }"
                      data-index="index"
                    >
                      {{ index + 1 }}
                    </div>
                  </div>

                  <!-- Song Info -->
                  <div class="results-card-info min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                        {{ song.title }}
                      </h3>
                      <i
                        v-if="hasUserVoted(song.id)"
                        class="pi pi-check-circle text-teal-600 dark:text-teal-400"
                        title="You voted for this song"
                      ></i>
                    </div>
                    <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-sm">
                      {{ song.author }}
                    </p>
                  </div>

                  <!-- Vote Count -->
                  <div class="results-card-votes text-center">
                    <div class="flex items-center justify-center gap-2 mb-1">
                      <i class="pi pi-heart text-teal-500 text-xl"></i>
                      <span class="text-3xl font-bold text-gray-900 dark:text-white">
                        {{ getVoteCount(song.id) }}
                      </span>
                    </div>
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {{ getVoteCount(song.id) === 1 ? "vote" : "votes" }}
                    </span>
                  </div>

                  <!-- Mark Performed Button (Admin only) -->
                  <div class="results-card-button">
                    <button
                      v-if="authStore.isAdmin && !getIsPerformed(song.id)"
                      class="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                      @click="handleMarkPerformed(song.id)"
                    >
                      <i class="pi pi-check-circle"></i>
                      Mark Performed
                    </button>
                    <div
                      v-else-if="getIsPerformed(song.id)"
                      class="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <i class="pi pi-check-circle"></i>
                      Performed
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
          </template>
        </div>

        <!-- Performed Songs View -->
        <div v-else-if="activeView === 'performed'">
          <div v-if="performedSongs.length === 0" class="text-center py-16">
            <div
              class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <i class="pi pi-check-circle text-3xl text-gray-400"></i>
            </div>
            <p class="text-gray-600 dark:text-gray-400 text-lg">
              No songs have been marked as performed yet
            </p>
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="(song, index) in performedSongs"
              :key="`${song.id}-${song.performedAt || index}`"
              class="bg-white dark:bg-gray-800 rounded-xl border-2 p-6 hover:shadow-md transition-all"
              :class="{
                'current-song-card border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-500':
                  index === 0 && !song.endedAt,
                'border-emerald-700 dark:border-emerald-800 bg-emerald-50/10 dark:bg-emerald-900/10':
                  index > 0 || song.endedAt
              }"
            >
              <div class="results-card-layout">
                <!-- Performance Order Badge / Currently Playing Icon -->
                <div class="results-card-rank">
                  <div
                    v-if="index === 0 && !song.endedAt"
                    class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white animate-pulse"
                    :class="
                      isKaraokeLive
                        ? 'bg-amber-500 dark:bg-amber-600'
                        : 'bg-green-500 dark:bg-green-600'
                    "
                    title="Currently being performed"
                  >
                    <i
                      :class="isKaraokeLive ? 'pi pi-microphone text-2xl' : 'pi pi-volume-up text-2xl'"
                    ></i>
                  </div>
                  <div
                    v-else
                    class="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center font-bold text-xl text-white bg-emerald-700 dark:bg-emerald-800"
                  >
                    {{ index + 1 }}
                  </div>
                </div>

                <!-- Song Info -->
                <div class="results-card-info min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                      {{ song.title }}
                    </h3>
                    <i
                      v-if="song.endedAt || index > 0"
                      class="pi pi-check-circle text-emerald-700 dark:text-emerald-500"
                      title="Performed"
                    ></i>
                  </div>
                  <p v-if="song.author" class="text-gray-600 dark:text-gray-400 text-sm">
                    {{ song.author }}
                  </p>
                  <p
                    v-if="song.performerName"
                    class="text-base font-oswald tracking-wide text-purple-600 dark:text-purple-300 mt-0.5 flex items-center gap-1"
                  >
                    <i class="pi pi-user" style="font-size: 12px"></i>
                    {{ song.performerName }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Performed at: {{ formatDateTime(song.performedAt) }}
                    <span v-if="song.endedAt" class="ml-2">
                      • Ended at: {{ formatDateTime(song.endedAt) }}
                    </span>
                  </p>
                </div>

                <!-- Vote Count -->
                <div v-if="!isKaraokeLive" class="results-card-votes text-center">
                  <div class="flex items-center justify-center gap-2 mb-1">
                    <i class="pi pi-heart text-teal-500 text-xl"></i>
                    <span class="text-3xl font-bold text-gray-900 dark:text-white">
                      {{ getVoteCount(song.id) }}
                    </span>
                  </div>
                  <span class="text-sm text-gray-500 dark:text-gray-400">
                    {{ getVoteCount(song.id) === 1 ? "vote" : "votes" }}
                  </span>
                </div>

                <!-- Set End Button (for currently playing) / Status badge (for completed) -->
                <div v-if="!isKaraokeLive" class="results-card-button">
                  <button
                    v-if="index === 0 && !song.endedAt && authStore.isAdmin"
                    class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                    @click="handleSetEnd(song.id)"
                  >
                    <i class="pi pi-stop-circle"></i>
                    Set end
                  </button>
                  <div
                    v-else
                    class="px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    <i class="pi pi-check-circle"></i>
                    Performed
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Extra Set Modal: shown when admin starts a set beyond predefined list -->
  <Dialog
    v-model:visible="showExtraSetModal"
    :draggable="false"
    modal
    header="Start Extra Set"
    pt:root:class="!border-0 !bg-transparent"
    :closable="true"
    @hide="pendingSetAutoPlay = false"
  >
    <div class="space-y-6">
      <div
        class="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg"
      >
        <p class="text-blue-800 dark:text-blue-200 font-medium flex items-center gap-2">
          <i class="pi pi-info-circle"></i>
          All predefined sets have been used.
        </p>
        <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
          You can define a duration for this extra set, or start it without a timer.
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Set duration (minutes) — leave empty for no timer
        </label>
        <InputNumber
          v-model="extraSetDurationMin"
          :min="1"
          :max="300"
          placeholder=""
          class="w-full"
          input-class="w-full"
          @keyup.enter="handleExtraSetConfirm(true)"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between gap-2">
        <Button
          label="Start with duration"
          severity="info"
          icon="pi pi-forward"
          :disabled="!extraSetDurationMin || extraSetDurationMin <= 0"
          @click="handleExtraSetConfirm(true)"
        />
        <Button
          label="Start without timer"
          severity="secondary"
          icon="pi pi-play"
          @click="handleExtraSetConfirm(false)"
        />
        <Button label="Cancel" severity="secondary" outlined @click="handleExtraSetModalCancel" />
      </div>
    </template>
  </Dialog>

  <!-- Set End Warning Modal -->
  <Dialog
    v-model:visible="showSetEndWarningModal"
    modal
    :draggable="false"
    header="Set Time Running Out"
    :closable="true"
    @hide="setEndWarningDismissed = true"
  >
    <div class="space-y-4">
      <!-- Remaining time banner -->
      <div
        class="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg"
      >
        <p class="text-amber-800 dark:text-amber-200 font-medium">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          Set {{ (activeEvent?.currentSetIndex || 0) + 1 }} is about to end!
        </p>
        <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
          Remaining time:
          <strong :class="setRemainingUrgency">{{
            setRemainingFormatted ?? "Set time over"
          }}</strong>
        </p>
      </div>

      <!-- Songs fitting in the current set -->
      <div v-if="upNextSong || lastFittingRankedSong">
        <p
          class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2"
        >
          Fitting in this set
        </p>
        <!-- Up Next (always fits — already locked in) -->
        <div
          v-if="upNextSong"
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 mb-1.5"
        >
          <i class="pi pi-lock text-amber-500 dark:text-amber-400 text-xs shrink-0"></i>
          <span class="text-sm font-medium text-gray-900 dark:text-white truncate">{{
            upNextSong.title
          }}</span>
          <span
            class="ml-auto text-xs text-amber-600 dark:text-amber-400 font-bold uppercase shrink-0"
            >Up Next</span
          >
        </div>
        <!-- Last ranked song that still fits -->
        <div
          v-if="lastFittingRankedSong"
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
        >
          <i class="pi pi-check-circle text-teal-500 text-xs shrink-0"></i>
          <span class="text-sm text-gray-800 dark:text-gray-200 truncate">{{
            lastFittingRankedSong.title
          }}</span>
          <span class="ml-auto text-xs text-gray-400 shrink-0">#{{ setEndLineIndex - 1 + 1 }}</span>
        </div>
      </div>

      <!-- No ranked songs currently fit -->
      <p v-else-if="!upNextSong" class="text-sm text-gray-500 dark:text-gray-400 italic">
        No more songs fit in the remaining time.
      </p>

      <!-- Candidate that would fit if the set is extended by one slot -->
      <div v-if="nextCandidateSong">
        <p
          class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2"
        >
          Would fit if extended by one song
        </p>
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700"
        >
          <i class="pi pi-plus-circle text-blue-500 dark:text-blue-400 text-xs shrink-0"></i>
          <span class="text-sm text-gray-800 dark:text-gray-200 truncate">{{
            nextCandidateSong.title
          }}</span>
        </div>
      </div>

      <p v-else-if="isSetlistActive" class="text-sm text-gray-500 dark:text-gray-400 italic">
        No more songs available in the voting list.
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Dismiss"
          severity="secondary"
          outlined
          @click="
            showSetEndWarningModal = false;
            setEndWarningDismissed = true;
          "
        />
        <Button
          v-if="nextCandidateSong"
          label="Fit one more song"
          icon="pi pi-plus-circle"
          severity="info"
          :loading="extendingSet"
          @click="extendSetByOneSong"
        />
      </div>
    </template>
  </Dialog>

  <KaraokePerformerModal
    v-model:visible="showKaraokePerformerModal"
    :song-title="pendingKaraokeSong?.title || ''"
    @confirm="handleKaraokePerformerConfirm"
  />
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useVotesStore } from "@/stores/votes";
import { useSocketStore } from "@/stores/socket";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { formatDateTime, formatDateWithWeekday } from "@/utils/dateFormat";
import {
  computeSetEndIndex,
  computeRemainingSetTime,
  formatCountdown,
  buildSongLengthMap
} from "@/utils/setlistCalculator";
import { filterSongsForEvent } from "@/utils/eventSongFilter";
import { isEventLive, getLiveEvents } from "@/utils/eventStatus";
import { isPrevoting, isKaraokeEvent } from "@/constants/eventTypes";
import KaraokePerformerModal from "@/components/admin/KaraokePerformerModal.vue";
import KaraokeQueueRow from "@/components/events/KaraokeQueueRow.vue";
import api from "@/services/api";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import InputNumber from "primevue/inputnumber";
import SleepingCat from "@/components/SleepingCat.vue";
import DancingCat from "@/components/DancingCat.vue";
import SongCard from "@/components/events/SongCard.vue";
import SetEndDivider from "@/components/SetEndDivider.vue";

const authStore = useAuthStore();
const isStaffUser = computed(() => authStore.isAdmin || authStore.isHelper);
const votesStore = useVotesStore();
const socketStore = useSocketStore();
const toast = useToast();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();
const activeEvent = ref(null);
const nextEvent = ref(null);
const liveEventsPicker = ref([]);
const eventLoadError = ref(null);
const songs = ref([]);
const loading = ref(false);
let joinedEventId = null;
const activeView = ref("vote"); // 'vote', 'results', or 'performed'
const togglingVoting = ref(false);
const endingEvent = ref(false);
const playingNextSong = ref(false);
const playingNextSongNoQueue = ref(false);
const clearingUpNext = ref(false);
const showKaraokePerformerModal = ref(false);
const pendingKaraokeSong = ref(null);

// ── Setlist timer state ──────────────────────────────────────────────────────
const setlistSettings = ref(null); // { pauseTime, defaultSongTime }
const setRemainingSeconds = ref(-1);
let setTimerInterval = null;
const showSetEndWarningModal = ref(false);
const setEndWarningDismissed = ref(false);
// const showPlayNextConfirmModal = ref(false);
// const playNextConfirmResolve = ref(null);

// ── Extra set modal (when admin starts a set beyond predefined list) ──────────
const showExtraSetModal = ref(false);
const extraSetDurationMin = ref(null); // minutes typed by admin (null = untimed)
const extendingSet = ref(false);
const pendingSetAutoPlay = ref(false); // true when prepare flow was initiated from "Start set"

const filters = ref({
  title: "",
  category: null,
  sortBy: null
});

const votingStartTime = computed(() => {
  if (!activeEvent.value?.votingStartTime) return null;
  return new Date(activeEvent.value.votingStartTime);
});

const votingEndTime = computed(() => {
  if (!activeEvent.value?.votingEndTime) return null;
  return new Date(activeEvent.value.votingEndTime);
});

const votingStatusText = computed(() => {
  if (!activeEvent.value) return "Unknown";

  const now = new Date();
  const start = votingStartTime.value;
  const end = votingEndTime.value;

  if (!start) return "Not started";
  if (now < start) return "Not started";
  if (end && now > end) return "Ended";
  return "Open";
});

const getVoteCount = (songId) => {
  if (!activeEvent.value) return 0;
  // Access the computed property correctly - it returns a function
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
  return songVote ? songVote.totalVotes : 0;
};

const getFirstVotedAt = (songId) => {
  if (!activeEvent.value) return null;
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.songId === songId || vote.song?.id === songId);
  return songVote?.firstVotedAt || null;
};

const hasUserVoted = (songId) => {
  if (!activeEvent.value) return false;
  return votesStore.hasUserVotedForSong(activeEvent.value.id, songId);
};

// Get performed status for a song from votes store
const getIsPerformed = (songId) => {
  if (!activeEvent.value) return false;
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
  const songVote = eventVotes.find((vote) => vote.song?.id === songId || vote.songId === songId);
  return songVote ? songVote.isPerformed : false;
};

// Results view: songs with votes that haven't been performed, sorted by votes (descending)
// Uses the backend's pre-sorted results to ensure consistent ordering
const sortedResults = computed(() => {
  if (!activeEvent.value) return [];

  // Get the pre-sorted results from the votes store (sorted by backend)
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);

  // Filter: only songs with votes (totalVotes > 0), not performed, and not disabled
  // The backend already filters disabled songs, but this is a safety net for stale cache
  const filtered = eventVotes.filter((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    const isDisabled = vote.song?.isDisabled || localSong?.isDisabled;
    return vote.totalVotes > 0 && !vote.isPerformed && !isDisabled;
  });

  // Map to include song details from our local songs array for additional info if needed
  return filtered.map((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    return {
      id: songId,
      title: vote.song?.title || localSong?.title || "Unknown",
      author: vote.song?.author || localSong?.author || "",
      originalArtist: vote.song?.originalArtist || localSong?.originalArtist || "",
      genre: vote.song?.genre || localSong?.genre || "",
      category: vote.song?.category || localSong?.category || "",
      voteCount: vote.totalVotes,
      isPerformed: vote.isPerformed,
      firstVotedAt: vote.firstVotedAt,
      performedAt: vote.performedAt,
      endedAt: vote.endedAt,
      songLength: vote.song?.songLength || localSong?.songLength || null
    };
  });
});

// Results filtered to exclude the up-next song (shown separately at position 0)
const sortedResultsFiltered = computed(() => {
  if (!upNextSong.value) return sortedResults.value;
  return sortedResults.value.filter((song) => song.id !== upNextSong.value.id);
});

// ── Setlist "Set End" line computation ──────────────────────────────────────
const isSetlistActive = computed(() => {
  const ev = activeEvent.value;
  return ev && ev.setlistEnabled && ev.currentSetStartedAt && setlistSettings.value;
});

const currentSetDuration = computed(() => {
  const ev = activeEvent.value;
  if (!ev?.setlistEnabled) return 0;
  const durations = Array.isArray(ev.setlistDurations) ? ev.setlistDurations : [];
  const idx = ev.currentSetIndex || 0;
  return idx < durations.length ? durations[idx] : 0;
});

const setEndLineIndex = computed(() => {
  if (!isSetlistActive.value || !currentSetDuration.value) return -1;
  const ev = activeEvent.value;

  const songLengthMap = buildSongLengthMap([
    ...sortedResultsFiltered.value,
    ...(upNextSong.value ? [upNextSong.value] : [])
  ]);

  return computeSetEndIndex(sortedResultsFiltered.value, {
    setDurationMinutes: currentSetDuration.value,
    pauseTimeSec: setlistSettings.value.pauseTime || 30,
    defaultSongTimeSec: setlistSettings.value.defaultSongTime || 240,
    upNextSong: upNextSong.value,
    currentSong: currentlyPlayingSong.value,
    setStartedAt: ev.currentSetStartedAt,
    songLengthMap
  });
});

const setRemainingFormatted = computed(() => {
  if (setRemainingSeconds.value === -1) return null; // -1 means no active set
  return formatCountdown(setRemainingSeconds.value);
});

const setRemainingUrgency = computed(() => {
  const secs = setRemainingSeconds.value;
  if (secs < 0) return "";
  if (secs <= 0) return "text-red-600 dark:text-red-400";
  const oneSongCost =
    (setlistSettings.value?.defaultSongTime || 240) + (setlistSettings.value?.pauseTime || 30);
  if (secs <= oneSongCost) return "text-red-600 dark:text-red-400";
  if (secs <= oneSongCost * 2) return "text-amber-600 dark:text-amber-400";
  return "text-green-600 dark:text-green-400";
});

// Check if the next song to be played would be the last one fitting the set,
// or if the set time has already been exceeded.
// With the divider placed BEFORE song at setEndLineIndex:
//   setEndLineIndex === 1  → only song 0 fits → it would be the last reserved song
//   setEndLineIndex === 0  → no ranked songs fit at all (set is effectively over)
//   setEndLineIndex === -1 → set time has been exceeded entirely
// All three states should prompt the admin about reserving the next "Up Next" song.
const isNextSongLastInSet = computed(() => {
  if (!isSetlistActive.value || !currentSetDuration.value) return false;
  const idx = setEndLineIndex.value;
  return idx >= -1 && idx <= 1;
});

// Last ranked song (from sortedResultsFiltered) that still fits in the set.
// setEndLineIndex points to the first song that does NOT fit, so the last fitting
// ranked song is at index setEndLineIndex - 1.
const lastFittingRankedSong = computed(() => {
  const idx = setEndLineIndex.value;
  if (idx >= 1) return sortedResultsFiltered.value[idx - 1] || null;
  return null;
});

// The first ranked song that does NOT currently fit — the candidate for "fit one more".
const nextCandidateSong = computed(() => {
  if (!isSetlistActive.value) return null;
  const idx = setEndLineIndex.value;
  // idx <= 0 means nothing from the ranked list fits → first song is the candidate
  if (idx <= 0) return sortedResultsFiltered.value[0] || null;
  // idx >= 1 → songs 0..idx-1 fit, song at idx is the first that doesn't
  return sortedResultsFiltered.value[idx] || null;
});

const categoryOptions = computed(() => {
  const categories = new Set();
  songs.value.forEach((song) => {
    if (song.categories && Array.isArray(song.categories)) {
      song.categories.forEach((cat) => categories.add(cat));
    } else if (song.category) {
      categories.add(song.category);
    }
  });
  return Array.from(categories)
    .sort()
    .map((c) => ({ label: c, value: c }));
});

const sortOptions = computed(() => [
  { label: "Most votes", value: "votes_desc" },
  { label: "Least votes", value: "votes_asc" },
  { label: "Title A-Z", value: "title_asc" },
  { label: "Title Z-A", value: "title_desc" }
]);

const getFirstVoteSort = (a, b) => {
  const aFirst = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
  const bFirst = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
  return aFirst - bFirst;
};

const filteredSongs = computed(() => {
  let result = [...songs.value];

  if (filters.value.title) {
    const query = filters.value.title.toLowerCase();
    result = result.filter(
      (song) =>
        song.title?.toLowerCase().includes(query) || song.author?.toLowerCase().includes(query)
    );
  }

  if (filters.value.category) {
    result = result.filter((song) => {
      if (song.categories && Array.isArray(song.categories)) {
        return song.categories.includes(filters.value.category);
      }
      return song.category === filters.value.category;
    });
  }

  result = result.map((song) => ({
    ...song,
    isPerformed: getIsPerformed(song.id),
    firstVotedAt: getFirstVotedAt(song.id)
  }));

  if (filters.value.sortBy) {
    result.sort((a, b) => {
      const aVotes = getVoteCount(a.id);
      const bVotes = getVoteCount(b.id);

      switch (filters.value.sortBy) {
        case "votes_desc":
          if (bVotes !== aVotes) return bVotes - aVotes;
          return getFirstVoteSort(a, b);
        case "votes_asc":
          if (aVotes !== bVotes) return aVotes - bVotes;
          return getFirstVoteSort(a, b);
        case "title_asc":
          return (a.title || "").localeCompare(b.title || "");
        case "title_desc":
          return (b.title || "").localeCompare(a.title || "");
        default:
          return 0;
      }
    });
  }

  return result;
});

const clearFilters = () => {
  filters.value = {
    title: "",
    category: null,
    sortBy: null
  };
};

// Get the currently playing song (first song in performedSongs without endedAt)
const currentlyPlayingSong = computed(() => {
  if (performedSongs.value.length === 0) return null;
  const firstSong = performedSongs.value[0];
  return firstSong && !firstSong.endedAt ? firstSong : null;
});

// Check if there's a currently being performed song (has performedAt but no endedAt)
const hasCurrentSong = computed(() => {
  return currentlyPlayingSong.value !== null;
});

// Get the up-next song (locked, will be performed next)
const upNextSong = computed(() => {
  if (!activeEvent.value) return null;
  const upNextId = votesStore.getUpNextSongId(activeEvent.value.id);
  if (!upNextId) return null;
  // Find song details from the local songs array
  const song = songs.value.find((s) => s.id === upNextId);
  if (!song) return null;
  return {
    ...song,
    voteCount: getVoteCount(upNextId)
  };
});

// Performed songs view: songs that have been performed, sorted by performedAt (descending - latest first)
// Songs without endedAt are considered "currently being performed" and appear first
const performedSongs = computed(() => {
  if (!activeEvent.value) return [];

  if (isKaraokeLive.value) {
    const playing = votesStore.getNowPlaying(activeEvent.value.id);
    const history = votesStore.getPerformedHistory(activeEvent.value.id);
    const mapEntry = (entry, endedAt) => ({
      id: entry.songId,
      performedSongId: entry.performedSongId,
      title: entry.song?.title || "Unknown",
      author: entry.song?.author || "",
      performerName: entry.performerName || "",
      performedAt: entry.performedAt,
      endedAt: endedAt ?? entry.endedAt ?? null,
      voteCount: 0
    });

    const items = [];
    if (playing) {
      items.push(mapEntry(playing, null));
    }
    history.forEach((entry) => items.push(mapEntry(entry, entry.endedAt)));
    return items;
  }

  // Get the results from the votes store
  const eventVotes = votesStore.getEventVotes(activeEvent.value.id);

  // Filter: only songs that have been performed
  const filtered = eventVotes.filter((vote) => {
    return vote.isPerformed && vote.performedAt;
  });

  // Map to include song details
  const mapped = filtered.map((vote) => {
    const songId = vote.songId || vote.song?.id;
    const localSong = songs.value.find((s) => s.id === songId);
    return {
      id: songId,
      title: vote.song?.title || localSong?.title || "Unknown",
      author: vote.song?.author || localSong?.author || "",
      originalArtist: vote.song?.originalArtist || localSong?.originalArtist || "",
      genre: vote.song?.genre || localSong?.genre || "",
      category: vote.song?.category || localSong?.category || "",
      voteCount: vote.totalVotes,
      isPerformed: vote.isPerformed,
      firstVotedAt: vote.firstVotedAt,
      performedAt: vote.performedAt,
      endedAt: vote.endedAt
    };
  });

  return mapped.sort((a, b) => {
    // First, prioritize songs without endedAt (currently being performed)
    if (!a.endedAt && b.endedAt) return -1;
    if (a.endedAt && !b.endedAt) return 1;

    // Then sort by performedAt timestamp (descending - latest first)
    return new Date(b.performedAt) - new Date(a.performedAt);
  });
});

const isPrevotingEvent = computed(() => isPrevoting(activeEvent.value?.eventType));
const isKaraokeLive = computed(() => isKaraokeEvent(activeEvent.value));

const karaokeNowPlaying = computed(() => {
  if (!activeEvent.value) return null;
  return votesStore.getNowPlaying(activeEvent.value.id);
});

const karaokeQueueEntries = computed(() => {
  if (!activeEvent.value) return [];
  return votesStore.getQueueEntries(activeEvent.value.id);
});

const showStartSetButton = computed(() => {
  if (isKaraokeLive.value) return false;
  if (!isPrevotingEvent.value) return true;
  // Pre-voting events: hide "Start set" / set-prep actions; keep "Play next song" if a song is active
  return hasCurrentSong.value;
});

// ── Set readiness indicators (for Start set button + prepared banner) ─────────
const preparedSetNumber = computed(() => (activeEvent.value?.currentSetIndex || 0) + 1);

const isPreviousSetExpired = computed(() => {
  return !hasCurrentSong.value && isSetlistActive.value && setRemainingSeconds.value <= 0;
});

const isSetPreparedAwaitingStart = computed(() => {
  const ev = activeEvent.value;
  if (!ev?.setlistEnabled || hasCurrentSong.value || ev.currentSetStartedAt) return false;
  return performedSongs.value.length > 0;
});

const startSetButtonLabel = computed(() => {
  if (hasCurrentSong.value) return "Play next song";
  if (isSetPreparedAwaitingStart.value) return "Start prepared set";
  return "Start set";
});

const startSetButtonIcon = computed(() => {
  if (hasCurrentSong.value) return "pi pi-forward";
  if (isSetPreparedAwaitingStart.value) return "pi pi-check-circle";
  return "pi pi-play";
});

const startSetButtonSeverity = computed(() => {
  if (hasCurrentSong.value) return "success";
  if (isSetPreparedAwaitingStart.value) return "info";
  if (isPreviousSetExpired.value) return "warn";
  return "success";
});

const startSetHelperText = computed(() => {
  if (hasCurrentSong.value) return null;
  if (isSetPreparedAwaitingStart.value) {
    const n = preparedSetNumber.value;
    if (currentSetDuration.value > 0) {
      return `Set ${n} is ready — first song will start the ${currentSetDuration.value} min timer`;
    }
    return `Set ${n} is ready — first song will start the set`;
  }
  if (isPreviousSetExpired.value) {
    return "Previous set ended — you'll be prompted to prepare the next set";
  }
  return null;
});

const handleVote = async (songId) => {
  if (!activeEvent.value) return;

  if (isKaraokeLive.value) {
    const song = songs.value.find((item) => item.id === songId);
    pendingKaraokeSong.value = song || { id: songId, title: "Unknown" };
    showKaraokePerformerModal.value = true;
    return;
  }

  const result = await votesStore.submitVote(activeEvent.value.id, songId);
  if (!result.success) {
    console.error("Vote failed:", result.message);
  }
};

const handleKaraokePerformerConfirm = async (performerName) => {
  if (!activeEvent.value || !pendingKaraokeSong.value) return;

  const songTitle = pendingKaraokeSong.value.title || "song";
  const songId = pendingKaraokeSong.value.id;

  const result = await votesStore.submitVote(activeEvent.value.id, songId, {
    performerName
  });

  pendingKaraokeSong.value = null;

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Queue error",
      detail: result.message || "Failed to add song to queue",
      life: 4000
    });
    return;
  }

  toast.add({
    severity: "success",
    summary: "Added to queue",
    detail: `${performerName} queued for ${songTitle}`,
    life: 3000
  });

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

const handleKaraokeStartSong = async (entry) => {
  if (!activeEvent.value || !entry) return;

  const result = await votesStore.markAsPerformed(activeEvent.value.id, entry.songId, {
    voteId: entry.voteId
  });

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Start song failed",
      detail: result.message || "Could not start song",
      life: 4000
    });
    return;
  }

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

const handleKaraokeMarkPerformed = async (entry) => {
  if (!activeEvent.value || !entry) return;

  const result = await votesStore.setEndTime(activeEvent.value.id, entry.songId, {
    performedSongId: entry.performedSongId
  });

  if (!result.success) {
    toast.add({
      severity: "error",
      summary: "Mark performed failed",
      detail: result.message || "Could not end performance",
      life: 4000
    });
    return;
  }

  await votesStore.fetchEventVotes(activeEvent.value.id);
};

const handleMarkPerformed = async (songId) => {
  if (!activeEvent.value) return;

  // If there's a currently playing song, mark it as ended first
  if (currentlyPlayingSong.value) {
    const currentSongId = currentlyPlayingSong.value.id;
    const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
    if (!endResult.success) {
      console.error("Failed to end current song:", endResult.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to end current song: " + (endResult.message || "Unknown error"),
        life: 5000
      });
      return;
    }
  }

  // Now mark the new song as performed
  const result = await votesStore.markAsPerformed(activeEvent.value.id, songId);
  if (result.success) {
    // Refetch votes to update the performed status
    await votesStore.fetchEventVotes(activeEvent.value.id);
    toast.add({
      severity: "success",
      summary: "Success",
      detail:
        "Song marked as performed" + (currentlyPlayingSong.value ? " and previous song ended" : ""),
      life: 3000
    });
  } else {
    console.error("Mark performed failed:", result.message);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to mark song as performed",
      life: 5000
    });
  }
};

const handleSetEnd = async (songId) => {
  if (!activeEvent.value) return;
  const result = await votesStore.setEndTime(activeEvent.value.id, songId);
  if (result.success) {
    // Refetch votes to update the ended status
    await votesStore.fetchEventVotes(activeEvent.value.id);
    // Force the set timer to 0 so "Prepare Next Set" becomes available immediately,
    // regardless of how much time the countdown still had left.
    if (isSetlistActive.value) {
      setRemainingSeconds.value = 0;
    }
    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Song end time has been set",
      life: 3000
    });
  } else {
    console.error("Set end failed:", result.message);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to set song end time",
      life: 5000
    });
  }
};

const handleClearUpNext = async () => {
  if (!activeEvent.value) return;
  clearingUpNext.value = true;
  try {
    const result = await votesStore.clearUpNext(activeEvent.value.id);
    if (result.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Unlocked",
        detail: "Song has been released back to the voting board",
        life: 3000
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.message || "Failed to unlock song",
        life: 5000
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to unlock song",
      life: 5000
    });
  } finally {
    clearingUpNext.value = false;
  }
};

const showPlayNextError = (error) => {
  console.error("Play next song failed:", error);
  toast.add({
    severity: "error",
    summary: "Error",
    detail: error.response?.data?.message || error.message || "Failed to play next song",
    life: 5000
  });
};

const handlePlayNextSong = async () => {
  if (!activeEvent.value || playingNextSong.value) return;

  // Guard: previous set timer has expired and no song is playing — prompt before
  // starting the next set. Does not fire when a set is already prepared (no active timer).
  // This fires when the button reads "Start set" (!hasCurrentSong).
  if (
    activeEvent.value.setlistEnabled &&
    Array.isArray(activeEvent.value.setlistDurations) &&
    activeEvent.value.setlistDurations.length > 0 &&
    !hasCurrentSong.value &&
    isSetlistActive.value &&
    setRemainingSeconds.value <= 0
  ) {
    confirm.require({
      message:
        "The current set has ended. Would you like to prepare the next set first, or start the song without a set timer?",
      header: "Set Not Prepared",
      icon: "pi pi-calendar",
      rejectClass: "p-button-secondary p-button-outlined",
      rejectLabel: "Start Without Set",
      acceptClass: "p-button-info",
      acceptLabel: "Set Up Next Set",
      accept: () => {
        handleStartNextSet({ autoPlay: true });
      },
      reject: () => {
        // Proceed directly without configuring a set
        executePlayNextSong();
      }
    });
    return;
  }

  // Safety guard: if the set is active, autoQueueNext is on, and the upcoming
  // reserved song would be the last to fit the set, ask the admin whether to
  // play with or without reserving the next "Up Next" song.
  if (
    isSetlistActive.value &&
    activeEvent.value.autoQueueNext &&
    isNextSongLastInSet.value &&
    hasCurrentSong.value
  ) {
    confirm.require({
      message:
        'The next song will be the last one fitting this set. Do you want to reserve another "Up Next" song after it, or play without reserving?',
      header: "Last Song in Set",
      icon: "pi pi-exclamation-triangle",
      rejectClass: "p-button-success",
      rejectLabel: "Play & Reserve Next",
      acceptClass: "p-button-warning",
      acceptLabel: "Play Without Reserving",
      accept: () => {
        // Play without reserving (no queue)
        handlePlayNextSongNoQueue();
      },
      reject: () => {
        // Normal flow - play and reserve
        executePlayNextSong();
      }
    });
    return;
  }

  await executePlayNextSong();
};

const executePlayNextSong = async () => {
  if (!activeEvent.value || playingNextSong.value) return;

  try {
    playingNextSong.value = true;

    // Step 1: If there's a currently being performed song, end it first
    const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
    const currentSong = eventVotes.find((vote) => {
      return vote.isPerformed && vote.performedAt && !vote.endedAt;
    });

    if (currentSong) {
      const currentSongId = currentSong.songId || (currentSong.song && currentSong.song.id);
      if (!currentSongId) {
        showPlayNextError(new Error("Could not determine current song ID"));
        return;
      }
      const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
      if (!endResult.success) {
        showPlayNextError(new Error(endResult.message || "Failed to end current song"));
        return;
      }
      await votesStore.fetchEventVotes(activeEvent.value.id);
    }

    // Step 2: Use the locked "up next" song if available, otherwise find highest-voted
    let nextSong = null;

    if (upNextSong.value) {
      nextSong = upNextSong.value;
    } else {
      const songsWithVotes = songs.value.map((song) => ({
        ...song,
        voteCount: getVoteCount(song.id),
        isPerformed: getIsPerformed(song.id),
        firstVotedAt: getFirstVotedAt(song.id)
      }));

      const availableSongs = songsWithVotes
        .filter((song) => !song.isPerformed && song.voteCount > 0)
        .sort((a, b) => {
          if (b.voteCount !== a.voteCount) return b.voteCount - a.voteCount;
          const aFirstVote = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVote = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVote - bFirstVote;
        });

      nextSong = availableSongs.length > 0 ? availableSongs[0] : null;
    }

    if (!nextSong) {
      toast.add({
        severity: "warn",
        summary: "No songs available",
        detail: "There are no songs with votes that haven't been performed yet",
        life: 5000
      });
      return;
    }

    // Step 3: Mark the song as performed
    const performResult = await votesStore.markAsPerformed(activeEvent.value.id, nextSong.id);

    if (performResult.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `"${nextSong.title}" is now being performed`,
        life: 3000
      });
    } else {
      showPlayNextError(new Error(performResult.message || "Failed to mark song as performed"));
    }
  } catch (error) {
    showPlayNextError(error);
  } finally {
    playingNextSong.value = false;
  }
};

// Handler for playing next song without auto-queueing (for set endings)
const handlePlayNextSongNoQueue = async () => {
  if (!activeEvent.value || playingNextSongNoQueue.value) return;

  try {
    playingNextSongNoQueue.value = true;

    // Step 1: If there's a currently being performed song, end it first
    const eventVotes = votesStore.getEventVotes(activeEvent.value.id);
    const currentSong = eventVotes.find((vote) => {
      return vote.isPerformed && vote.performedAt && !vote.endedAt;
    });

    if (currentSong) {
      const currentSongId = currentSong.songId || (currentSong.song && currentSong.song.id);
      if (!currentSongId) {
        showPlayNextError(new Error("Could not determine current song ID"));
        return;
      }
      const endResult = await votesStore.setEndTime(activeEvent.value.id, currentSongId);
      if (!endResult.success) {
        showPlayNextError(new Error(endResult.message || "Failed to end current song"));
        return;
      }
      await votesStore.fetchEventVotes(activeEvent.value.id);
    }

    // Step 2: Use the locked "up next" song if available, otherwise find highest-voted
    let nextSong = null;

    if (upNextSong.value) {
      nextSong = upNextSong.value;
    } else {
      const songsWithVotes = songs.value.map((song) => ({
        ...song,
        voteCount: getVoteCount(song.id),
        isPerformed: getIsPerformed(song.id),
        firstVotedAt: getFirstVotedAt(song.id)
      }));

      const availableSongs = songsWithVotes
        .filter((song) => !song.isPerformed && song.voteCount > 0)
        .sort((a, b) => {
          if (b.voteCount !== a.voteCount) return b.voteCount - a.voteCount;
          const aFirstVote = a.firstVotedAt ? new Date(a.firstVotedAt).getTime() : Infinity;
          const bFirstVote = b.firstVotedAt ? new Date(b.firstVotedAt).getTime() : Infinity;
          return aFirstVote - bFirstVote;
        });

      nextSong = availableSongs.length > 0 ? availableSongs[0] : null;
    }

    if (!nextSong) {
      toast.add({
        severity: "warn",
        summary: "No songs available",
        detail: "There are no songs with votes that haven't been performed yet",
        life: 5000
      });
      return;
    }

    // Step 3: Mark the song as performed WITH skipAutoQueue flag
    const performResult = await votesStore.markAsPerformed(activeEvent.value.id, nextSong.id, {
      skipAutoQueue: true
    });

    if (performResult.success) {
      await votesStore.fetchEventVotes(activeEvent.value.id);
      toast.add({
        severity: "success",
        summary: "Last Song",
        detail: `"${nextSong.title}" is now being performed (no next song queued)`,
        life: 3000
      });
    } else {
      showPlayNextError(new Error(performResult.message || "Failed to mark song as performed"));
    }
  } catch (error) {
    showPlayNextError(error);
  } finally {
    playingNextSongNoQueue.value = false;
  }
};

const fetchEventForRoute = async () => {
  try {
    loading.value = true;
    eventLoadError.value = null;
    liveEventsPicker.value = [];

    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    const response = await api.get("/api/events");
    if (response.data.success) {
      const events = response.data.data.events || [];
      const now = new Date();
      const live = getLiveEvents(events, now);
      const routeEventId = route.params.eventId;

      if (!routeEventId) {
        if (live.length === 0) {
          activeEvent.value = null;
        } else if (live.length === 1) {
          await router.replace(`/events/live/${live[0].id}`);
          return;
        } else {
          activeEvent.value = null;
          liveEventsPicker.value = live;
        }
      } else {
        const event = events.find((e) => String(e.id) === String(routeEventId));

        if (!event || !isEventLive(event, now)) {
          if (activeEvent.value && joinedEventId) {
            socketStore.leaveEvent(joinedEventId);
            joinedEventId = null;
          }
          activeEvent.value = null;
          songs.value = [];
          eventLoadError.value = event ? "This event is not currently live." : "Event not found.";
        } else {
          if (joinedEventId && joinedEventId !== event.id) {
            socketStore.leaveEvent(joinedEventId);
            joinedEventId = null;
          }

          activeEvent.value = event;

          try {
            const songsResponse = await api.get(`/api/songs`);
            if (songsResponse.data.success) {
              const fetchedSongs = songsResponse.data.data.songs || [];
              songs.value = filterSongsForEvent(
                fetchedSongs.filter((song) => !song.isDisabled),
                activeEvent.value
              );
            }
          } catch (error) {
            console.error("Failed to fetch songs:", error);
          }

          await votesStore.fetchEventVotes(activeEvent.value.id);
          await votesStore.fetchUserVotes(activeEvent.value.id);

          if (!socketStore.isConnected) {
            socketStore.connect();
          }
          socketStore.joinEvent(activeEvent.value.id);
          joinedEventId = activeEvent.value.id;
        }
      }

      const liveIds = new Set(live.map((e) => e.id));
      const upcomingEvents = events
        .filter((event) => !event.isDisabled && event.eventStartTime && !liveIds.has(event.id))
        .map((event) => {
          let eventDate = null;
          if (event.eventStartTime) {
            eventDate = new Date(event.eventStartTime);
          }
          return { ...event, eventDate };
        })
        .filter((event) => event.eventDate && event.eventDate >= now)
        .sort((a, b) => a.eventDate - b.eventDate);

      nextEvent.value = upcomingEvents.length > 0 ? upcomingEvents[0] : null;
    }
  } catch (error) {
    console.error("Failed to fetch events:", error);
  } finally {
    loading.value = false;
  }
};

// Format event start time
const formatEventStartTime = (event) => {
  if (event.eventStartTime) {
    return formatDateWithWeekday(event.eventStartTime);
  }
  return "";
};

const toggleVoting = async () => {
  if (!activeEvent.value || togglingVoting.value) return;

  try {
    togglingVoting.value = true;
    const newVotingDisabledStatus = !activeEvent.value.isVotingDisabled;

    const response = await api.put(`/api/events/${activeEvent.value.id}`, {
      ...activeEvent.value,
      isVotingDisabled: newVotingDisabledStatus
    });

    if (response.data.success) {
      // Update local state
      activeEvent.value.isVotingDisabled = newVotingDisabledStatus;

      toast.add({
        severity: "success",
        summary: "Success",
        detail: newVotingDisabledStatus ? "Voting has been stopped" : "Voting has been resumed",
        life: 3000
      });
    } else {
      console.error("Failed to toggle voting:", response.data.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: response.data.message || "Failed to update voting status",
        life: 5000
      });
    }
  } catch (error) {
    console.error("Failed to toggle voting:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.response?.data?.message || "Failed to update voting status",
      life: 5000
    });
  } finally {
    togglingVoting.value = false;
  }
};

const confirmEndEvent = () => {
  if (!activeEvent.value) return;

  confirm.require({
    message:
      "Are you sure you want to end this event? This will stop all voting and close the event.",
    header: "End Event Confirmation",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    rejectLabel: "Cancel",
    acceptLabel: "End Event",
    accept: () => {
      endEvent();
    }
  });
};

const endEvent = async () => {
  if (!activeEvent.value || endingEvent.value) return;

  try {
    endingEvent.value = true;
    const now = new Date();

    const response = await api.put(`/api/events/${activeEvent.value.id}`, {
      ...activeEvent.value,
      isDisabled: true, // Stop all voting
      votingEndTime: now.toISOString() // Set voting end time to now
    });

    if (response.data.success) {
      // Update local state
      activeEvent.value.isDisabled = true;
      activeEvent.value.votingEndTime = now.toISOString();

      toast.add({
        severity: "success",
        summary: "Event Ended",
        detail: "The event has been ended. All voting has been stopped.",
        life: 5000
      });

      // Refresh the page to show the updated state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      console.error("Failed to end event:", response.data.message);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: response.data.message || "Failed to end event",
        life: 5000
      });
    }
  } catch (error) {
    console.error("Failed to end event:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.response?.data?.message || "Failed to end event",
      life: 5000
    });
  } finally {
    endingEvent.value = false;
  }
};

// ── Setlist timer management ────────────────────────────────────────────────
const startSetTimer = () => {
  stopSetTimer();
  setTimerInterval = setInterval(() => {
    if (!isSetlistActive.value || !currentSetDuration.value) {
      setRemainingSeconds.value = -1;
      return;
    }
    const remaining = computeRemainingSetTime(
      currentSetDuration.value,
      activeEvent.value.currentSetStartedAt
    );
    setRemainingSeconds.value = remaining;

    // Show warning modal when less than 8 minutes remain, re-triggering after each performed song
    if (!setEndWarningDismissed.value && remaining > 0) {
      if (remaining <= 480 && !showSetEndWarningModal.value) {
        showSetEndWarningModal.value = true;
      }
    }
  }, 1000);
};

const stopSetTimer = () => {
  if (setTimerInterval) {
    clearInterval(setTimerInterval);
    setTimerInterval = null;
  }
};

const fetchSetlistSettings = async () => {
  try {
    const response = await api.get("/api/settings/setlist");
    if (response.data.success) {
      setlistSettings.value = response.data.data;
    }
  } catch (err) {
    console.warn("Failed to fetch setlist settings:", err.message);
  }
};

// Handle set changes from socket
const handleSetChanged = (data) => {
  if (activeEvent.value && data.eventId === activeEvent.value.id) {
    activeEvent.value = {
      ...activeEvent.value,
      currentSetIndex: data.currentSetIndex,
      currentSetStartedAt: data.currentSetStartedAt,
      ...(data.setlistDurations !== undefined ? { setlistDurations: data.setlistDurations } : {})
    };
    setEndWarningDismissed.value = false;
  }
};

// True when clicking "Start Next Set" would go beyond the predefined set list
const isStartingExtraSet = computed(() => {
  const ev = activeEvent.value;
  if (!ev) return false;
  const durations = Array.isArray(ev.setlistDurations) ? ev.setlistDurations : [];
  return (ev.currentSetIndex || 0) + 1 >= durations.length;
});

// Handle "Prepare Next Set" button (prepare only — no auto-play)
const handleStartNextSet = async ({ autoPlay = false } = {}) => {
  if (!activeEvent.value) return;

  pendingSetAutoPlay.value = autoPlay;

  // If the next set would exceed the predefined list, ask the admin for a duration
  if (isStartingExtraSet.value) {
    extraSetDurationMin.value = null;
    showExtraSetModal.value = true;
    return;
  }

  const shouldAutoPlay = pendingSetAutoPlay.value;
  pendingSetAutoPlay.value = false;
  await executeStartNextSet(undefined, shouldAutoPlay);
};

const handleExtraSetModalCancel = () => {
  pendingSetAutoPlay.value = false;
  showExtraSetModal.value = false;
};

// Called after the modal is confirmed (with or without a duration)
const handleExtraSetConfirm = async (withDuration) => {
  const shouldAutoPlay = pendingSetAutoPlay.value;
  pendingSetAutoPlay.value = false;
  showExtraSetModal.value = false;
  const duration = withDuration && extraSetDurationMin.value > 0 ? extraSetDurationMin.value : 0;
  await executeStartNextSet(duration, shouldAutoPlay);
};

const executeStartNextSet = async (extraDuration = undefined, shouldAutoPlay = false) => {
  const result = await votesStore.startNextSet(activeEvent.value.id, extraDuration);
  if (result.success) {
    await fetchEventForRoute();
    setEndWarningDismissed.value = false;
    if (shouldAutoPlay) {
      const setNumber = (activeEvent.value?.currentSetIndex || 0) + 1;
      toast.add({
        severity: "success",
        summary: "Next Set",
        detail: `Set ${setNumber} prepared — starting first song`,
        life: 3000
      });
      await executePlayNextSong();
    } else {
      toast.add({
        severity: "success",
        summary: "Next Set",
        detail: "Next set prepared",
        life: 3000
      });
    }
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: result.message || "Failed to start next set",
      life: 5000
    });
  }
};

// Extend the current set's duration by exactly one song slot (defaultSongTime + pauseTime)
// so the set-end line moves forward by one, allowing one more song to fit.
const extendSetByOneSong = async () => {
  if (!activeEvent.value || extendingSet.value) return;
  extendingSet.value = true;
  try {
    const songCostSec =
      (setlistSettings.value?.defaultSongTime || 240) + (setlistSettings.value?.pauseTime || 30);
    const extraMinutes = Math.ceil(songCostSec / 60);
    const currentIdx = activeEvent.value.currentSetIndex || 0;
    const durations = [
      ...(Array.isArray(activeEvent.value.setlistDurations)
        ? activeEvent.value.setlistDurations
        : [])
    ];
    durations[currentIdx] = (durations[currentIdx] || 0) + extraMinutes;

    const response = await api.put(`/api/events/${activeEvent.value.id}`, {
      ...activeEvent.value,
      setlistDurations: durations
    });
    if (response.data.success) {
      activeEvent.value = { ...activeEvent.value, setlistDurations: durations };
      showSetEndWarningModal.value = false;
      setEndWarningDismissed.value = false; // allow re-trigger if still in warning zone
      toast.add({
        severity: "success",
        summary: "Set extended",
        detail: `Set extended by ${extraMinutes} min — one more song can now fit`,
        life: 3000
      });
    } else {
      throw new Error(response.data.message || "Failed to extend set");
    }
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: err.response?.data?.message || err.message || "Failed to extend set",
      life: 5000
    });
  } finally {
    extendingSet.value = false;
  }
};

// Wrapper for song_performed that also updates set tracking data on the active event
const handleSongPerformedWithSetUpdate = async (data) => {
  // Update set tracking on the active event
  if (activeEvent.value && data.eventId === activeEvent.value.id) {
    if (data.currentSetStartedAt !== undefined) {
      activeEvent.value = {
        ...activeEvent.value,
        currentSetStartedAt: data.currentSetStartedAt,
        currentSetIndex: data.currentSetIndex ?? activeEvent.value.currentSetIndex
      };
    }
    // Reset the warning gate so the modal can re-appear for the next song
    // if the timer is still under 8 minutes
    setEndWarningDismissed.value = false;
    showSetEndWarningModal.value = false;
  }
  // Delegate to the store handler for votes/upNext
  await votesStore.handleSongPerformed(data);
};

watch(
  () => route.params.eventId,
  async () => {
    stopSetTimer();
    setRemainingSeconds.value = -1;
    setEndWarningDismissed.value = false;
    showSetEndWarningModal.value = false;
    await fetchEventForRoute();
    startSetTimer();
  }
);

// Ensure Results tab always shows fresh data even if a socket update was missed
watch(activeView, async (newView) => {
  if (newView === "results" && activeEvent.value) {
    await votesStore.fetchEventVotes(activeEvent.value.id);
  }
});

onMounted(async () => {
  if (authStore.isAuthenticated && localStorage.getItem("token")) {
    // Ensure socket is connected first
    if (!socketStore.isConnected) {
      socketStore.connect();
    }

    // Register socket listeners AFTER socket is initialized
    socketStore.on("vote_updated", votesStore.handleVoteUpdated);
    socketStore.on("song_performed", handleSongPerformedWithSetUpdate);
    socketStore.on("song_ended", votesStore.handleSongEnded);
    socketStore.on("set_changed", handleSetChanged);

    await fetchSetlistSettings();
    await fetchEventForRoute();

    // Start the set countdown timer
    startSetTimer();
  }
});

onUnmounted(() => {
  stopSetTimer();
  socketStore.off("vote_updated", votesStore.handleVoteUpdated);
  socketStore.off("song_performed", handleSongPerformedWithSetUpdate);
  socketStore.off("song_ended", votesStore.handleSongEnded);
  socketStore.off("set_changed", handleSetChanged);

  if (joinedEventId) {
    socketStore.leaveEvent(joinedEventId);
    joinedEventId = null;
  }
});
</script>

<style scoped>
/* Results card grid layout */
.results-card-layout {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "rank info votes"
    "button button button";
  gap: 0.5rem 1rem;
  align-items: center;
}

.results-card-rank {
  grid-area: rank;
}
.results-card-info {
  grid-area: info;
}
.results-card-votes {
  grid-area: votes;
}
.results-card-button {
  grid-area: button;
}

@media (min-width: 500px) {
  .results-card-layout {
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    grid-template-areas: "rank info votes button";
  }
}
</style>
